import { useBreakpoint } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { type PropsWithChildren, useMemo } from "react";

import Footer from "@/components/shared/layout/footer/Footer";
import { LoadingState } from "@/components/shared/layout/LoadingState";
import MobileBlocker from "@/components/shared/layout/MobileBlocker";
import { PageHeader } from "@/components/shared/layout/PageHeader";
import { PageLayoutDesktop } from "@/components/shared/layout/PageLayoutDesktop";
import { Sidebar } from "@/components/shared/layout/sidebar/Sidebar";
import { RouteConfig } from "@/config/route.config";
import { useAuthAndOnboardingCheck } from "@/hooks/useAuthAndOnboardingCheck";
import { useDeviceType } from "@/hooks/useDeviceType";
import { DemoContext } from "@/providers/demo/DemoContext";
import { RoutingUtils } from "@/util/routing.utils";

import { Navbar } from "./navbar/Navbar";

export const PageWrapper = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const isDesktop = useBreakpoint("xl");
  const isTablet = useBreakpoint("md");
  const { isMobile } = useDeviceType();

  const { shouldShowSidebarLayout: showSidebarLayout, isCheckingAuth } = useAuthAndOnboardingCheck();
  const { isLoading: isDemoLoading } = DemoContext.useDemo();

  const isSettingsRoute = useMemo(() => {
    return router.pathname === RouteConfig.Settings.slice(0, -1);
  }, [router.pathname]);

  const isGuestEditRoute = useMemo(() => {
    return router.pathname.startsWith(RouteConfig.Guest.EditPositions.slice(0, -1));
  }, [router.pathname]);

  const isEditRoute = useMemo(() => {
    return router.pathname.startsWith(RouteConfig.Edit.Index.slice(0, -1)) || isGuestEditRoute;
  }, [router.pathname, isGuestEditRoute]);

  const isPublicProfileRoute = useMemo(() => {
    return router.pathname === RouteConfig.ViewProfile.slice(0, -1);
  }, [router.pathname]);

  const shouldShowFooter = useMemo(() => {
    return ![
      `${RouteConfig.Signin}verify`,
      `${RouteConfig.Signup}verify`,
      `${RouteConfig.Signup}sso-callback`,
      `${RouteConfig.Signin}sso-callback`,
      "/postsso",
      "/watchlist",
      RouteConfig.BugReport.slice(0, -1),
    ].includes(router.pathname);
  }, [router.pathname]);

  const shouldShowRegularNavbar = useMemo(() => {
    // Don't show regular navbar in settings or demo mode
    if (isSettingsRoute || isEditRoute) return false;

    const authRoutes = [
      RouteConfig.Onboarding.slice(0, -1),
      RouteConfig.Signup.slice(0, -1),
      RouteConfig.Signin.slice(0, -1),
      RouteConfig.BugReport.slice(0, -1),
    ];

    const betaCheckRoute = RouteConfig.BetaCheck.slice(0, -1);

    const isAuthRoute = authRoutes.some((route) => router.pathname.startsWith(route));

    // Hide navbar on mobile for auth routes
    if (!isTablet && isAuthRoute) return false;

    // Show navbar on tablet+ for auth routes, or on mobile for other routes
    return isAuthRoute || !isDesktop || router.pathname === betaCheckRoute;
  }, [router.pathname, isDesktop, isSettingsRoute, isEditRoute, isTablet]);

  const shouldApplyPageGradient = useMemo(() => {
    return (
      !RoutingUtils.isPathInRoutes(router.pathname, ["Onboarding"]) &&
      !RoutingUtils.isPathInRoutes(router.pathname, ["Signin"]) &&
      !RoutingUtils.isPathInRoutes(router.pathname, ["Signup"])
    );
  }, [router.pathname]);

  const shouldShowSidebarLayout = useMemo(() => {
    // Show sidebar in demo mode only if user is authenticated, or for authenticated users
    return isDesktop && showSidebarLayout && !isEditRoute;
  }, [isDesktop, showSidebarLayout, isEditRoute]);

  const getPageHeaderTitle = useMemo(() => {
    if (isSettingsRoute) return "Settings";
    if (isEditRoute) return "Profile settings";
    return "Settings";
  }, [isSettingsRoute, isEditRoute]);

  const getPageHeaderBackButton = useMemo(() => {
    if (isSettingsRoute) return { label: "Back", action: () => router.back() };
    if (isGuestEditRoute) return { label: "Profile", action: () => router.push(RouteConfig.Guest.Index) };
    if (isEditRoute) return { label: "Profile", action: () => router.push(RouteConfig.Profile) };
    return { label: "Back", action: () => router.back() };
  }, [isSettingsRoute, isGuestEditRoute, isEditRoute, router]);

  if (isCheckingAuth || isDemoLoading) {
    return <LoadingState />;
  }

  if (isMobile || !isTablet) {
    return <MobileBlocker />;
  }

  if (isDesktop && shouldShowSidebarLayout) {
    return <PageLayoutDesktop>{children}</PageLayoutDesktop>;
  }
  if (!isDesktop) {
    return <MobileBlocker />;
  }

  return (
    <div
      className={cx(
        "flex overflow-x-clip bg-elevation-fill-default-0",
        (shouldShowRegularNavbar || isSettingsRoute || isEditRoute) && "flex-col",
        shouldShowSidebarLayout && "flex-row",
      )}
    >
      {shouldShowRegularNavbar && <Navbar />}
      {shouldShowSidebarLayout && <Sidebar />}
      {(isSettingsRoute || isEditRoute) && (
        <PageHeader
          title={getPageHeaderTitle}
          button={getPageHeaderBackButton}
        />
      )}
      <div
        className={cx(
          "print-main-content relative flex min-h-[calc(100vh-72px)]! flex-col xl:min-h-screen!",
          shouldShowSidebarLayout ? "ml-[320px] w-[calc(100%-320px)] max-w-[calc(100vw-320px)] flex-1" : "w-full",
          (isSettingsRoute || isEditRoute) && "min-h-[calc(100vh-78px)]! bg-elevation-fill-default-0",
          router.asPath === RouteConfig.Guest.Index && "bg-secondary-500",
          shouldApplyPageGradient && "page-gradient",
        )}
      >
        <main
          className={cx(
            "flex flex-1 flex-col overflow-x-clip",
            shouldShowSidebarLayout && "mx-auto w-full",
            shouldShowSidebarLayout && !isPublicProfileRoute && "max-w-[1600px]",
          )}
        >
          {children}
        </main>
        {shouldShowFooter && <Footer />}
      </div>
    </div>
  );
};
